<!-- sections/WeightsSection.vue -->
<script setup lang="ts">
import { computed } from 'vue';
import BaseSection from '../components/BaseSection.vue';
import ConnxField, { ErrorUpdate } from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import { numericAboveZero } from '@/validation';
import { getUnitAffix } from '../utils/validation-helpers';

interface Props {
  weights?: {
    label: string;
    labels: string[];
    values: (string | number)[];
    unit?: string;
  };
  errors?: Map<string, string | null>;
  showTitle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => new Map(),
  showTitle: true,
});

const emit = defineEmits<{
  'field-error': [{ name: string; error: string | null }];
}>();

const weightsError = computed(() => {
  if (!props.weights?.values) return '';

  const sum = props.weights.values.reduce((total: number, val) => {
    const numVal = typeof val === 'string' ? parseFloat(val) || 0 : val;
    return total + numVal;
  }, 0) as number;

  if (Math.abs(sum - 100) >= 0.01) {
    emit('field-error', { name: 'weights', error: 'Total weight must equal 100' });
    return 'Total weight must equal 100';
  }

  emit('field-error', { name: 'weights', error: null });
  return '';
});

const handleFieldError = (error: ErrorUpdate) => {
  emit('field-error', error);
};
</script>

<template>
  <BaseSection :title="weights?.label" :should-show="!!weights" :show-title="showTitle">
    <div class="tw-w-auto">
      <div class="tw-grid tw-grid-cols-[auto_auto] tw-gap-y-4 tw-gap-x-6 tw-items-center tw-w-fit">
        <div class="tw-flex tw-items-center tw-gap-x-4">
          <h5 class="tw-mb-0 Field__label tw-text-base">Metric</h5>
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <h5 class="tw-mb-0 Field__label tw-text-base"> Weight {{ getUnitAffix(weights?.unit) }} </h5>
        </div>

        <template v-for="(label, index) in weights?.labels" :key="label">
          <div class="tw-flex tw-items-center tw-gap-x-4">
            <label :for="`weights[${index}]`" class="tw-m-0">
              {{ label }}
            </label>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <ConnxField
              :name="`weights[${index}]`"
              v-model="weights!.values[index]"
              class="tw-m-0 tw-w-52"
              :rules="numericAboveZero"
              required
              :type="InputType.Number"
              @update:error="handleFieldError"
            />
          </div>
        </template>

        <div class="tw-flex tw-items-center tw-gap-x-4"></div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <span v-if="weightsError" class="Field__error tw-text-connx-danger tw-text-sm tw-w-full tw-font-semibold">
            {{ weightsError }}
          </span>
        </div>
      </div>
    </div>
  </BaseSection>
</template>
